import { getUserAttributes } from '@choco-admin/auth';
import { type EnvironmentConfig } from '@choco-admin/utils';
import {
  asyncWithLDProvider,
  useLDClient,
} from 'launchdarkly-react-client-sdk';
import React, { useEffect } from 'react';

/**
 * LaunchDarkly for the portal.
 *
 * Two things about the portal shape this has to respect. It is one build that
 * talks to eight Choco environments, and LaunchDarkly's client-side ID is per
 * LaunchDarkly environment, so the ID comes from the selected environment's
 * config rather than from a single build-time variable. And switching
 * environment signs the user out and reloads the page, so the client only ever
 * needs starting once per load: there is no live re-initialisation to get wrong.
 */

/**
 * Builds the provider for `envConfig`, or null when that environment has no
 * client-side ID.
 *
 * Null is a real answer, not a failure: production has no ID until its build
 * variable is set, and starting no client is better than starting one against
 * another environment's flags. Callers render children unwrapped, and every flag
 * falls back to its default.
 */
export const createLaunchDarklyProvider = async (
  envConfig: EnvironmentConfig
): Promise<React.FC<{ children: React.ReactNode }> | null> => {
  if (!envConfig.launchDarklyClientId) return null;

  try {
    return await asyncWithLDProvider({
      clientSideID: envConfig.launchDarklyClientId,
      // Anonymous to begin with. The signed-in email arrives later, and
      // `IdentifyPortalUser` hands it over then, which is how per-person
      // targeting keeps working the way it did in the legacy admin.
      context: { kind: 'user', key: 'anonymous', anonymous: true },
      // Flags resolve before the app renders, so no control flashes editable and
      // then locks. Bounded so a slow or blocked LaunchDarkly cannot hold the
      // portal hostage: on timeout the SDK serves defaults.
      timeout: 3,
      options: { streaming: true },
    });
  } catch (error) {
    // A failure here must not stop the portal booting. Every flag falls back to
    // its default, which for the integration guard means it stays on.
    // eslint-disable-next-line no-console
    console.warn('LaunchDarkly did not start; using flag defaults', error);
    return null;
  }
};

/**
 * Hands the signed-in person to LaunchDarkly so flags targeted at them resolve.
 *
 * The legacy admin did the same in `useIdentifyUser`, with the Cognito key, the
 * username and the email, and the platform as a custom attribute. Targeting in
 * LaunchDarkly is by email, so the email is the part that matters.
 */
export const IdentifyPortalUser: React.FC = () => {
  const ldClient = useLDClient();

  useEffect(() => {
    let cancelled = false;

    // Guarded rather than returned early, so every path leaves the same cleanup
    // behind and a late `identify` cannot land after unmount.
    if (ldClient) {
      const identify = async () => {
        try {
          const attributes = await getUserAttributes();
          if (cancelled || !attributes.email) return;

          await ldClient.identify({
            kind: 'user',
            key: attributes.sub ?? attributes.email,
            name: attributes.name,
            email: attributes.email,
            platform: 'ChocoPortal',
          });
        } catch (error) {
          // Unidentified means flags resolve for the anonymous context, which
          // for anything targeted at a person means off. Safe, and worth a line
          // in the console rather than silence.
          // eslint-disable-next-line no-console
          console.warn('Could not identify the user to LaunchDarkly', error);
        }
      };

      identify();
    }

    return () => {
      cancelled = true;
    };
  }, [ldClient]);

  return null;
};
